/* ============================================================
   Toee Tire — Homepage interactions (vanilla JS, no deps)
   Powers the new "tt-" homepage sections. Every initializer is
   idempotent (guarded with a data flag) and re-runs on the
   Shopify theme-editor `shopify:section:load` event so sections
   added or reordered in the customizer keep working.
   ============================================================ */
(function () {
  "use strict";

  // Each tt- section links this file; guard so the boot logic and the
  // theme-editor listener are only ever wired once per page.
  if (window.ToeeHome) { window.ToeeHome.initAll(document); return; }

  var reduceMotion = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  /* ── Scroll reveal ─────────────────────────────────────── */
  var revealObserver = null;
  function getRevealObserver() {
    if (revealObserver || !("IntersectionObserver" in window)) return revealObserver;
    revealObserver = new IntersectionObserver(function (entries, obs) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { e.target.classList.add("is-in"); obs.unobserve(e.target); }
      });
    }, { threshold: 0.12 });
    return revealObserver;
  }
  function initReveal(root) {
    var io = getRevealObserver();
    var nodes = root.querySelectorAll(".tt-reveal:not([data-tt-obs])");
    nodes.forEach(function (el) {
      el.setAttribute("data-tt-obs", "1");
      if (!io) { el.classList.add("is-in"); return; }
      var r = el.getBoundingClientRect();
      if (r.top < window.innerHeight && r.bottom > 0) { el.classList.add("is-in"); return; }
      io.observe(el);
    });
  }

  /* ── Count-up for numeric stats ────────────────────────── */
  function runCountUp(el) {
    var target = parseFloat(el.getAttribute("data-count-to"));
    var suffix = el.getAttribute("data-count-suffix") || "";
    if (isNaN(target)) return;
    if (reduceMotion) { el.textContent = target + suffix; return; }
    var duration = 1100, start = null;
    function ease(p) { return 1 - Math.pow(1 - p, 3); }
    function tick(now) {
      if (start === null) start = now;
      var p = Math.min(1, (now - start) / duration);
      el.textContent = Math.round(ease(p) * target) + suffix;
      if (p < 1) requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
  }
  function initCountUp(root) {
    var nodes = root.querySelectorAll("[data-count-to]:not([data-tt-count])");
    nodes.forEach(function (el) {
      el.setAttribute("data-tt-count", "1");
      if (!("IntersectionObserver" in window)) { runCountUp(el); return; }
      var io = new IntersectionObserver(function (entries, obs) {
        entries.forEach(function (e) {
          if (e.isIntersecting) { runCountUp(el); obs.disconnect(); }
        });
      }, { threshold: 0.4 });
      io.observe(el);
    });
  }

  /* ── Size search (Tires / Wheels) ──────────────────────── */
  function initSearch(root) {
    var holders = root.querySelectorAll("[data-toee-search]:not([data-tt-init])");
    holders.forEach(function (holder) {
      holder.setAttribute("data-tt-init", "1");
      var seg = holder.querySelector("[data-toee-search-seg]");
      var modeWraps = holder.querySelectorAll("[data-mode-fields]");
      function activate(mode) {
        if (seg) seg.querySelectorAll("button").forEach(function (b) {
          b.classList.toggle("active", b.getAttribute("data-mode") === mode);
        });
        modeWraps.forEach(function (w) {
          w.style.display = (w.getAttribute("data-mode-fields") === mode) ? "contents" : "none";
        });
        // A mode may own a panel under the grid (the Vehicle tab's results);
        // assets/toee-vehicle-lookup.js loads its year list on first show.
        holder.querySelectorAll("[data-mode-panel]").forEach(function (p) {
          var on = p.getAttribute("data-mode-panel") === mode;
          p.hidden = !on;
          if (on && p.hasAttribute("data-toee-vehicle") && window.ToeeVehicleLookup) window.ToeeVehicleLookup.activate(p);
        });
        var sizeStock = holder.querySelector("[data-toee-size-stock]");
        if (sizeStock && holder.querySelector('[data-mode-panel="' + mode + '"]')) sizeStock.hidden = true;
      }
      // The Vehicle tab breathes (toee-home.css) until the visitor has opened it once.
      var seenKey = "toee-vehicle-tab-seen";
      function newFeatureSeen() { try { return !!localStorage.getItem(seenKey); } catch (e) { return false; } }
      function markNewFeatureSeen() {
        try { localStorage.setItem(seenKey, "1"); } catch (e) { /* private mode: it breathes again next visit */ }
        document.querySelectorAll(".tt-seg button[data-new-feature]").forEach(function (n) { n.removeAttribute("data-new-feature"); });
      }
      if (seg && newFeatureSeen()) seg.querySelectorAll("button[data-new-feature]").forEach(function (n) { n.removeAttribute("data-new-feature"); });
      if (seg) seg.querySelectorAll("button").forEach(function (b) {
        b.addEventListener("click", function () {
          if (b.hasAttribute("data-new-feature")) markNewFeatureSeen();
          activate(b.getAttribute("data-mode"));
        });
      });
      // default active mode = first mode wrapper
      if (modeWraps.length) activate(modeWraps[0].getAttribute("data-mode-fields"));

      // Match Shopify's tag→URL slug: lowercase, non-alphanumeric runs → "-", trim "-".
      // e.g. "12.5" → "12-5", "5×114.3" → "5-114-3", "BLACK MACHINE FACE" → "black-machine-face".
      function ttHandleize(str) {
        return String(str == null ? "" : str)
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-+|-+$/g, "");
      }

      function comboApi() {
        return window.ToeeSizeCombo || null;
      }

      function selectValues(selects) {
        return Array.prototype.map.call(selects, function (s) { return s.value || ""; });
      }

      function isLiveHandle(value) {
        return /^[a-z0-9]+(?:-[a-z0-9]+)*_[a-z0-9]+(?:-[a-z0-9]+)*$/.test(value || "");
      }

      function handlesFromSelects(selects, api) {
        return Array.prototype.map.call(selects, function (s) {
          if (!s.value) return "";
          if (isLiveHandle(s.value)) return s.value;
          return api.tagHandle(s.getAttribute("data-tag-prefix") || "", s.value);
        }).filter(function (h) { return !!h; });
      }

      function snizeFiltersFromSelects(selects) {
        return Array.prototype.map.call(selects, function (s) {
          if (!s.value) return null;
          var opt = s.options[s.selectedIndex];
          return {
            prefix: s.getAttribute("data-tag-prefix") || "",
            value: opt ? String(opt.textContent || "").trim() : ""
          };
        }).filter(function (f) { return f && f.prefix && f.value; });
      }

      function fillSelect(select, options, anyLabel) {
        var keep = (window.ToeeSizeCombo && window.ToeeSizeCombo.pruneSelected(options, select.value)) || "";
        // The select's own first option is its localized "Any" placeholder
        // (rendered server-side, e.g. dealer_pro.cascade.any). Prefer it
        // over anyLabel — the field's own accessible label (e.g. "Aspect
        // ratio") — so repopulating a downstream select on every cascade
        // change doesn't quietly replace "Any" with the field name after
        // its first use (dp-3-report.md fix round, finding #5).
        var existingAny = select.options && select.options[0] ? select.options[0].textContent : "";
        select.innerHTML = "";
        var any = document.createElement("option");
        any.value = "";
        any.textContent = existingAny || anyLabel || "Any";
        select.appendChild(any);
        (options || []).forEach(function (opt) {
          var o = document.createElement("option");
          o.value = opt.handle;
          o.textContent = opt.label;
          select.appendChild(o);
        });
        select.value = keep;
      }

      function mergeGroups(target, extra) {
        Object.keys(extra || {}).forEach(function (key) {
          var seen = {};
          var out = (target[key] || []).concat(extra[key] || []).filter(function (item) {
            if (!item || !item.handle || seen[item.handle]) return false;
            seen[item.handle] = true;
            return true;
          });
          target[key] = out;
        });
        return target;
      }

      function lockHandles(wrap) {
        return String(wrap.getAttribute("data-lock-handles") || "")
          .split(/[,\s]+/)
          .filter(function (h) { return !!h; });
      }

      function comboBases(wrap) {
        var primary = wrap.getAttribute("data-combo-url") || wrap.getAttribute("data-target-url") || "";
        var extra = wrap.getAttribute("data-combo-extra-url") || "";
        var out = [];
        if (primary) out.push(primary);
        if (extra && extra !== primary) out.push(extra);
        return out;
      }

      function fetchComboGroups(url, wrap, gen, api) {
        return fetch(url, { credentials: "same-origin" })
          .then(function (res) {
            if (wrap.comboGen !== gen) return null;
            return res.ok ? res.json() : Promise.reject(res.status);
          })
          .then(function (json) {
            if (wrap.comboGen !== gen || !json) return null;
            var parsed = api.parseSizeOptions(json);
            var groups = parsed.groups || {};
            var pages = parsed.pages || 1;
            var path = parsed.path || "";
            var pending = [];
            for (var p = 2; p <= pages; p++) {
              pending.push(
                fetch(path + "?view=size-options&page=" + p, { credentials: "same-origin" })
                  .then(function (r) { return r.ok ? r.json() : Promise.reject(r.status); })
                  .then(function (j) { mergeGroups(groups, api.parseSizeOptions(j).groups); })
              );
            }
            return Promise.all(pending).then(function () { return groups; });
          });
      }

      function loadCombo(wrap, throughIndex) {
        wrap.comboGen = wrap.comboGen || 0;
        wrap.comboGen += 1;
        var gen = wrap.comboGen;

        var api = comboApi();
        if (!api || wrap.getAttribute("data-combo-src") !== "size-options") return;
        if ((wrap.getAttribute("data-target-type") || "tag") !== "tag") return;
        var selects = wrap.querySelectorAll("select");
        var values = selectValues(selects);
        var cleared = api.rebuildAfterChange(values, throughIndex);
        selects.forEach(function (s, i) { s.value = cleared[i] || ""; });
        var selected = handlesFromSelects(
          Array.prototype.filter.call(selects, function (_, i) { return i <= throughIndex; }),
          api
        );
        var bases = comboBases(wrap);
        if (!bases.length) return;
        Promise.all(bases.map(function (base) {
          return fetchComboGroups(api.comboUrl(base, selected), wrap, gen, api);
        }))
          .then(function (groupSets) {
            if (wrap.comboGen !== gen) return;
            var groups = {};
            (groupSets || []).forEach(function (set) {
              if (set) mergeGroups(groups, set);
            });
            selects.forEach(function (s, i) {
              if (i <= throughIndex) return;
              var prefix = s.getAttribute("data-tag-prefix") || "";
              var anyLabel = s.getAttribute("aria-label") || "";
              fillSelect(s, groups[prefix] || [], anyLabel);
            });
          })
          .catch(function () {
            if (wrap.comboGen !== gen) return;
            selects.forEach(function (s, i) {
              if (i <= throughIndex) return;
              fillSelect(s, [], s.getAttribute("aria-label") || "");
            });
          });
      }

      modeWraps.forEach(function (wrap) {
        var selects = wrap.querySelectorAll("select");
        selects.forEach(function (select, index) {
          select.addEventListener("change", function () {
            loadCombo(wrap, index);
          });
        });
      });
      var btn = holder.querySelector("[data-toee-search-submit]");
      if (btn) btn.addEventListener("click", function () {
        var active = holder.querySelector('[data-mode-fields]:not([style*="none"])') || modeWraps[0];
        if (!active) return;
        if (active.getAttribute("data-target-type") === "vehicle") {
          var vehiclePanel = holder.querySelector('[data-mode-panel="' + active.getAttribute("data-mode-fields") + '"]');
          if (vehiclePanel && window.ToeeVehicleLookup) window.ToeeVehicleLookup.submit(vehiclePanel);
          return;
        }
        var base = active.getAttribute("data-target-url") || "/search";
        var type = active.getAttribute("data-target-type") || "tag";
        var selects = active.querySelectorAll("select");
        var keywords = []; // raw values, for the search page
        selects.forEach(function (s) { if (s.value) keywords.push(s.value); });

        // Tag-filtered size search shows warehouse stock inline instead of
        // redirecting to the collection page only when this specific mode
        // opts in via its own "Show stock inline" block setting — not just
        // because the target is a tag-filtered collection (a Wheels mode is
        // "tag" too, but has no size-stock view backing it and must redirect
        // like before unless it explicitly turns inline results on). Section
        // setting data-result-mode="redirect" still force-disables inline
        // results section-wide (e.g. for the homepage).
        var resultMode = holder.getAttribute("data-result-mode") || "inline";
        var inlineResults = active.getAttribute("data-inline-results") === "true";
        var stockPanel = holder.querySelector("[data-toee-size-stock]");
        if (inlineResults && resultMode === "inline" && stockPanel && window.ToeeSizeStock && comboApi()) {
          var sizeHandles = handlesFromSelects(selects, comboApi());
          // Only go inline once every one of this mode's dropdowns is
          // filled — a partial selection (e.g. Width only) mixes a
          // collection-wide total with a top-6-variant warehouse
          // breakdown that don't share the same denominator.
          if (selects.length > 0 && sizeHandles.length === selects.length) {
            window.ToeeSizeStock.run(stockPanel, base, sizeHandles);
            return;
          }
        }

        var url;
        var pending = null;
        if (type === "search") {
          url = base + (base.indexOf("?") > -1 ? "&" : "?") + "q=" + encodeURIComponent(keywords.join(" "));
        } else if (comboApi()) {
          var api = comboApi();
          var handles = api.concatHandles(lockHandles(active), handlesFromSelects(selects, api));
          var filters = snizeFiltersFromSelects(selects);
          var extra = active.getAttribute("data-combo-extra-url") || "";
          if (!extra || extra === base) {
            url = api.snizeSearchUrl(base, filters);
          } else {
            function countFor(collectionUrl) {
              return fetch(api.comboUrl(collectionUrl, handles), { credentials: "same-origin" })
                .then(function (res) { return res.ok ? res.json() : { productCount: 0 }; })
                .then(function (json) { return api.parseSizeOptions(json).productCount || 0; })
                .catch(function () { return 0; });
            }
            pending = Promise.all([countFor(base), countFor(extra)]).then(function (counts) {
              return api.snizeSearchUrl(
                api.pickSearchCollection(base, extra, counts[0], counts[1]),
                filters
              );
            });
          }
        } else {
          // If the combo module is missing, never attempt to rebuild tag handles
          // from dropdown labels. Just navigate to the bare collection URL.
          url = base.replace(/\/$/, "");
        }
        if (pending) {
          pending.then(function (dest) { window.location.href = dest; });
        } else {
          window.location.href = url;
        }
      });
    });
  }

  /* ── Category tabs ─────────────────────────────────────── */
  function initTabs(root) {
    var groups = root.querySelectorAll("[data-toee-tabs]:not([data-tt-init])");
    groups.forEach(function (group) {
      group.setAttribute("data-tt-init", "1");
      var tabs = group.querySelectorAll("[data-cat-tab]");
      var panes = group.querySelectorAll("[data-cat-pane]");
      function activate(name) {
        tabs.forEach(function (t) { t.classList.toggle("active", t.getAttribute("data-cat-tab") === name); });
        panes.forEach(function (p) {
          var on = p.getAttribute("data-cat-pane") === name;
          p.hidden = !on;
          if (on) {
            p.querySelectorAll(".tt-reveal").forEach(function (el) { el.classList.add("is-in"); });
          }
        });
      }
      tabs.forEach(function (t) {
        t.addEventListener("click", function () { activate(t.getAttribute("data-cat-tab")); });
      });
      if (tabs.length) activate(tabs[0].getAttribute("data-cat-tab"));
    });
  }

  /* ── Testimonials carousel ─────────────────────────────── */
  function initCarousel(root) {
    var wraps = root.querySelectorAll("[data-toee-carousel]:not([data-tt-init])");
    wraps.forEach(function (wrap) {
      wrap.setAttribute("data-tt-init", "1");
      var track = wrap.querySelector("[data-toee-track]");
      var prev = wrap.querySelector("[data-toee-prev]");
      var next = wrap.querySelector("[data-toee-next]");
      var dots = wrap.querySelectorAll("[data-toee-dot]");
      if (!track) return;
      function cards() {
        return track.querySelectorAll(".tt-quote-card");
      }
      function activeIndex() {
        var list = cards();
        if (!list.length) return 0;
        var left = track.scrollLeft;
        var best = 0;
        var bestDist = Infinity;
        for (var i = 0; i < list.length; i++) {
          var dist = Math.abs(list[i].offsetLeft - left);
          if (dist < bestDist) { bestDist = dist; best = i; }
        }
        return best;
      }
      function updatePager() {
        var index = activeIndex();
        dots.forEach(function (dot, i) {
          var on = i === index;
          dot.classList.toggle("is-active", on);
          if (on) dot.setAttribute("aria-current", "true");
          else dot.removeAttribute("aria-current");
        });
      }
      function updateEdges() {
        var atStart = track.scrollLeft <= 4;
        var atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
        if (prev) prev.disabled = atStart;
        if (next) next.disabled = atEnd;
        updatePager();
      }
      function scrollToIndex(index) {
        var list = cards();
        if (!list[index]) return;
        track.scrollTo({ left: list[index].offsetLeft, behavior: "smooth" });
      }
      function scrollByCard(dir) {
        scrollToIndex(activeIndex() + dir);
      }
      if (prev) prev.addEventListener("click", function () { scrollByCard(-1); });
      if (next) next.addEventListener("click", function () { scrollByCard(1); });
      dots.forEach(function (dot) {
        dot.addEventListener("click", function () {
          scrollToIndex(parseInt(dot.getAttribute("data-toee-dot"), 10) || 0);
        });
      });
      track.addEventListener("scroll", updateEdges, { passive: true });
      window.addEventListener("resize", updateEdges);
      updateEdges();
    });
  }

  /* ── FAQ accordion ─────────────────────────────────────── */
  function initAccordion(root) {
    var accs = root.querySelectorAll("[data-toee-acc]:not([data-tt-init])");
    accs.forEach(function (acc) {
      acc.setAttribute("data-tt-init", "1");
      var items = acc.querySelectorAll(".tt-acc-item");
      items.forEach(function (item) {
        var q = item.querySelector(".tt-acc-q");
        if (!q) return;
        q.addEventListener("click", function () {
          var isOpen = item.classList.contains("open");
          items.forEach(function (o) {
            o.classList.remove("open");
            var oq = o.querySelector(".tt-acc-q");
            if (oq) oq.setAttribute("aria-expanded", "false");
          });
          if (!isOpen) { item.classList.add("open"); q.setAttribute("aria-expanded", "true"); }
        });
      });
    });
  }

  /* ── Interactive warehouse map (Google Maps) ──────────── */
  function escapeMapText(value) {
    return String(value || "").replace(/[&<>"']/g, function (ch) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ch];
    });
  }

  function loadGoogleMaps(key) {
    if (window.google && window.google.maps && window.google.maps.Map) {
      return Promise.resolve();
    }
    key = String(key || "").trim();
    if (!key) return Promise.reject(new Error("missing maps key"));
    if (window.__toeeMapsLoad) return window.__toeeMapsLoad;
    window.__toeeMapsLoad = new Promise(function (resolve, reject) {
      var callbackName = "__toeeGoogleMapsReady";
      window[callbackName] = function () {
        resolve();
        try { delete window[callbackName]; } catch (e) { window[callbackName] = undefined; }
      };
      var script = document.createElement("script");
      script.async = true;
      script.setAttribute("data-toee-google-maps", "1");
      script.src = "https://maps.googleapis.com/maps/api/js?key=" + encodeURIComponent(key) + "&callback=" + callbackName;
      script.onerror = function () { reject(new Error("maps script failed")); };
      document.head.appendChild(script);
    });
    return window.__toeeMapsLoad;
  }

  function initOneMap(panel) {
    var mapEl = panel.querySelector("[data-toee-map]");
    if (!mapEl || !window.google || !window.google.maps) return;
    var locations;
    try { locations = JSON.parse(mapEl.getAttribute("data-locations") || "[]"); }
    catch (e) { locations = []; }
    if (!locations.length) return;
    var pinSvg = '<svg xmlns="http://www.w3.org/2000/svg" width="30" height="40" viewBox="0 0 30 40">' +
      '<path d="M15 0C6.7 0 0 6.7 0 15c0 10.3 15 25 15 25s15-14.7 15-25C30 6.7 23.3 0 15 0z" fill="#e02424"/>' +
      '<circle cx="15" cy="15" r="6" fill="#fff"/></svg>';
    var icon = {
      url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(pinSvg),
      scaledSize: new google.maps.Size(30, 40),
      anchor: new google.maps.Point(15, 40)
    };
    var first = locations[0];
    var map = new google.maps.Map(mapEl, {
      center: { lat: Number(first.lat), lng: Number(first.lng) },
      zoom: 8,
      gestureHandling: "cooperative",
      zoomControl: true,
      zoomControlOptions: { position: google.maps.ControlPosition.LEFT_TOP },
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      clickableIcons: false,
      backgroundColor: "#dfe7ef"
    });
    var info = new google.maps.InfoWindow();
    var bounds = new google.maps.LatLngBounds();
    var markers = locations.map(function (l) {
      var pos = { lat: Number(l.lat), lng: Number(l.lng) };
      bounds.extend(pos);
      var marker = new google.maps.Marker({ position: pos, map: map, icon: icon, title: l.city || "" });
      marker.addListener("click", function () {
        info.setContent("<strong>" + escapeMapText(l.city) + "</strong><br>" + escapeMapText(l.addr));
        info.open({ map: map, anchor: marker });
      });
      return marker;
    });
    function fitAll() {
      if (locations.length < 2) {
        map.setCenter(bounds.getCenter());
        map.setZoom(13);
        return;
      }
      // Up to 1139px the address card sits under the map (toee-home.css); on
      // desktop it overlays the right of the map, so the eastern pins
      // (Oakville, North York) need that strip left clear.
      var cardBelow = window.matchMedia("(max-width: 1139px)").matches;
      map.fitBounds(bounds, cardBelow
        ? { top: 48, right: 48, bottom: 48, left: 48 }
        : { top: 64, right: 330, bottom: 64, left: 48 });
    }
    fitAll();
    var userPicked = false;
    function refreshMapSize() {
      google.maps.event.trigger(map, "resize");
      if (!userPicked) fitAll();
    }
    setTimeout(refreshMapSize, 250);
    window.addEventListener("resize", refreshMapSize);
    panel.querySelectorAll("[data-loc-index]").forEach(function (row) {
      row.addEventListener("click", function () {
        var i = parseInt(row.getAttribute("data-loc-index"), 10);
        var l = locations[i];
        if (!l || !markers[i]) return;
        userPicked = true;
        map.setZoom(13);
        map.panTo({ lat: Number(l.lat), lng: Number(l.lng) });
        google.maps.event.trigger(markers[i], "click");
      });
    });
  }
  function initMaps(root) {
    var panels = root.querySelectorAll("[data-toee-map-panel]:not([data-tt-init])");
    panels.forEach(function (panel) {
      panel.setAttribute("data-tt-init", "1");
      var mapEl = panel.querySelector("[data-toee-map]");
      if (!mapEl) return;
      loadGoogleMaps(mapEl.getAttribute("data-maps-key")).then(function () {
        initOneMap(panel);
      }).catch(function () {});
    });
  }

  /* ── Phone header: away while reading down, back on the first scroll up ── */
  // The sticky header (dealer bar + logo row, ~92px) takes over a tenth of a phone
  // screen. It stays put while the menu is open or something in it has focus.
  function initHeaderAutoHide() {
    if (!window.matchMedia) return;
    var phone = window.matchMedia("(max-width: 767px)");
    var root = document.documentElement;
    var last = window.pageYOffset;
    var queued = false;
    function update() {
      queued = false;
      var y = window.pageYOffset;
      var d = y - last;
      if (Math.abs(d) < 6) return; // let small jitter add up
      var header = document.querySelector(".shopify-section-header");
      var busy = document.body.classList.contains("no-scroll") ||
        (header && document.activeElement && header.contains(document.activeElement));
      root.classList.toggle("tt-hdr-away", phone.matches && d > 0 && y > 160 && !busy);
      last = y;
    }
    window.addEventListener("scroll", function () {
      if (!queued) { queued = true; window.requestAnimationFrame(update); }
    }, { passive: true });
  }

  /* ── Boot ──────────────────────────────────────────────── */
  function initAll(root) {
    root = root || document;
    initReveal(root);
    initCountUp(root);
    initSearch(root);
    initTabs(root);
    initCarousel(root);
    initAccordion(root);
    initMaps(root);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () { initAll(document); });
  } else {
    initAll(document);
  }
  initHeaderAutoHide(); // page-wide, once (initAll re-runs per section)
  // Theme editor: re-init a section when it is (re)loaded.
  document.addEventListener("shopify:section:load", function (e) { initAll(e.target); });

  window.ToeeHome = { initAll: initAll };
})();
